import React, { useState, useMemo, useEffect } from "react";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
import {
  Card,
  SectionTitle,
  CategoryIcon,
  T,
  CATEGORY_LABELS,
} from "./dashboardUtils";

function DataExposureTable({ categories }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 600 : false,
  );

  const enriched = useMemo(
    () =>
      categories.map((c) => ({ ...c, label: CATEGORY_LABELS[c.tag] ?? c.tag })),
    [categories],
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const colTemplate = isMobile ? "1fr 80px" : "1fr 110px";
  const px = isMobile ? 1.5 : 2.5;
  const iconSize = isMobile ? 13 : 14;

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: colTemplate,
          px,
          py: isMobile ? 0.75 : 1,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        {["Data Type", "Protected"].map((h) => (
          <Typography
            key={h}
            variant="caption"
            sx={{
              fontSize: "0.68rem",
              fontWeight: 700,
              color: T.faint,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              textAlign: h === "Data Type" ? "left" : "center",
            }}
          >
            {h}
          </Typography>
        ))}
      </Box>
      <Box sx={isMobile ? { overflow: "hidden" } : undefined}>
        {enriched.map((cat, i) => (
          <Box
            key={cat.tag}
            sx={{
              display: "grid",
              gridTemplateColumns: colTemplate,
              px,
              py: 1.25,
              borderBottom:
                i < enriched.length - 1 ? `1px solid ${T.border}` : "none",
              alignItems: "center",
              bgcolor: T.surface,
              ...(!isMobile && {
                transition: "background 0.15s",
                cursor: "pointer",
                "&:hover": { bgcolor: T.accentBg },
              }),
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={isMobile ? 1 : 1.25}
            >
              <CategoryIcon tag={cat.tag} size={iconSize} />
              <Typography
                variant="body2"
                sx={{ fontSize: "0.875rem", fontWeight: 500, color: T.ink }}
              >
                {cat.label}
              </Typography>
            </Stack>
            <Typography
              variant={isMobile ? "caption" : "body2"}
              sx={{
                textAlign: "center",
                fontWeight: 500,
                color: (cat.protected ?? 0) > 0 ? T.muted : T.faint,
                ...(isMobile && { display: "block" }),
              }}
            >
              {(cat.protected ?? 0) > 0 ? cat.protected : "—"}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
}

export default function ProtectedDataCard({ isLoading, categories }) {
  return (
    <Card sx={{ mb: 2.5 }}>
      <SectionTitle
        title="Your Estimated Protected Data"
        subtitle="Data brokers don’t always disclose all of the information they hold, these figures represent our best estimate of what was removed."
      />
      {isLoading ? (
        <Box sx={{ p: 2.5 }}>
          {[...Array(6)].map((_, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                py: 1.25,
                borderBottom: i < 5 ? `1px solid ${T.border}` : "none",
              }}
            >
              <Skeleton variant="circular" width={14} height={14} />
              <Skeleton variant="text" width={130} height={16} />
              <Box flex={1} />
              <Skeleton variant="rounded" width={100} height={5} />
            </Stack>
          ))}
        </Box>
      ) : (
        <DataExposureTable categories={categories} />
      )}
    </Card>
  );
}
